import type {
  AppFeatures,
  KeyboardInsetsPayload,
  KeyboardLayoutMode,
  Platform,
} from "./types";

export interface KeyboardInsetsState {
  /** `features.keyboardInsetsBridge === true` 时为 true */
  bridgeEnabled: boolean;
  visible: boolean;
  height: number;
  layoutMode: KeyboardLayoutMode;
  duration?: number;
  /**
   * Phase 1：仅 `web-inset` 时为 true；`native-resize` 下 Web 不得再加 keyboard padding。
   */
  shouldApplyWebInsetPadding: boolean;
}

export const KEYBOARD_INSETS_IDLE: KeyboardInsetsState = {
  bridgeEnabled: false,
  visible: false,
  height: 0,
  layoutMode: "native-resize",
  shouldApplyWebInsetPadding: false,
};

export function isKeyboardInsetsBridgeEnabled(
  keyboardInsetsBridge: boolean | undefined,
): boolean {
  return keyboardInsetsBridge === true;
}

/**
 * Phase1/Phase2 键盘 inset 是否应对当前环境生效。
 * 需同时满足：iOS 壳内 + 原生 `keyboardInsetsBridge === true`。
 */
export function isKeyboardInsetsSupported(
  platform: Platform,
  features: Pick<AppFeatures, "keyboardInsetsBridge">,
): boolean {
  return (
    platform === "ios" &&
    isKeyboardInsetsBridgeEnabled(features.keyboardInsetsBridge)
  );
}

/** 由 App features 决定默认 layoutMode（Phase 2：`keyboardWebInset`） */
export function resolveDefaultKeyboardLayoutMode(
  features: Pick<AppFeatures, "keyboardWebInset">,
): KeyboardLayoutMode {
  return features.keyboardWebInset === true ? "web-inset" : "native-resize";
}

let lastAppliedKeyboardInsetBottom = -1;
/** 键盘会话内取最大高度，输入中联想栏变高时不反复改 padding（避免整页 reflow） */
let peakKeyboardInsetBottom = 0;

/** 由 Web 在 Bridge 回调里写 CSS 变量（勿用原生 evaluateJavaScript） */
export function applyKeyboardInsetToDom(bottomPx: number): void {
  if (typeof document === "undefined") {
    return;
  }
  const raw = Number.isFinite(bottomPx) ? Math.max(0, Math.round(bottomPx)) : 0;
  const bottom = raw > 0 ? Math.max(peakKeyboardInsetBottom, raw) : 0;
  if (raw === 0) {
    peakKeyboardInsetBottom = 0;
  } else {
    peakKeyboardInsetBottom = bottom;
  }
  if (bottom === lastAppliedKeyboardInsetBottom) {
    return;
  }
  lastAppliedKeyboardInsetBottom = bottom;
  document.documentElement.style.setProperty(
    "--keyboard-inset-bottom",
    `${bottom}px`,
  );
}

export function getKeyboardInsetBottomFromCSS(): number {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }
  const style = getComputedStyle(document.documentElement);
  const raw = style.getPropertyValue("--keyboard-inset-bottom") || "0";
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export function parseKeyboardInsetsPayload(
  data: unknown,
  options?: { defaultVisible?: boolean },
): KeyboardInsetsPayload | null {
  if (data == null || typeof data !== "object") {
    return null;
  }
  const record = data as Record<string, unknown>;
  const heightRaw = record.height ?? record.bottom;
  const height =
    typeof heightRaw === "number"
      ? heightRaw
      : parseInt(String(heightRaw ?? "0"), 10);
  const safeHeight = Number.isFinite(height) ? Math.max(0, height) : 0;

  let visible: boolean;
  if (typeof record.visible === "boolean") {
    visible = record.visible;
  } else if (options?.defaultVisible !== undefined) {
    visible = options.defaultVisible;
  } else {
    visible = safeHeight > 0;
  }

  const layoutMode: KeyboardLayoutMode =
    record.layoutMode === "web-inset" ? "web-inset" : "native-resize";

  const duration =
    typeof record.duration === "number" && Number.isFinite(record.duration)
      ? record.duration
      : undefined;

  return {
    visible,
    height: visible ? safeHeight : 0,
    layoutMode,
    duration,
  };
}

export function buildKeyboardInsetsState(
  bridgeEnabled: boolean,
  payload: Pick<KeyboardInsetsPayload, "visible" | "height" | "layoutMode"> & {
    duration?: number;
  },
): KeyboardInsetsState {
  const layoutMode = payload.layoutMode ?? "native-resize";
  return {
    bridgeEnabled,
    visible: payload.visible,
    height: payload.visible ? payload.height : 0,
    layoutMode,
    duration: payload.duration,
    shouldApplyWebInsetPadding:
      bridgeEnabled && layoutMode === "web-inset" && payload.visible,
  };
}

/**
 * web-inset 下键盘已可见时的高度微调由 CSS 变量驱动，无需 React 重渲染。
 */
export function shouldSkipKeyboardInsetsReactUpdate(
  prev: KeyboardInsetsState,
  next: KeyboardInsetsState,
): boolean {
  return (
    prev.bridgeEnabled &&
    next.bridgeEnabled &&
    prev.layoutMode === "web-inset" &&
    next.layoutMode === "web-inset" &&
    prev.visible &&
    next.visible &&
    prev.height !== next.height
  );
}

export function mergeKeyboardInsetsState(
  prev: KeyboardInsetsState,
  patch: Partial<KeyboardInsetsPayload>,
): KeyboardInsetsState {
  if (!prev.bridgeEnabled) {
    return prev;
  }
  const visible =
    typeof patch.visible === "boolean" ? patch.visible : prev.visible;
  const height =
    typeof patch.height === "number"
      ? Math.max(0, patch.height)
      : visible
        ? prev.height
        : 0;
  const layoutMode = patch.layoutMode ?? prev.layoutMode;
  const duration = patch.duration ?? prev.duration;
  const nextVisible = visible;
  const nextHeight = visible ? height : 0;
  if (
    prev.visible === nextVisible &&
    prev.height === nextHeight &&
    prev.layoutMode === layoutMode &&
    prev.duration === duration
  ) {
    return prev;
  }
  return buildKeyboardInsetsState(prev.bridgeEnabled, {
    visible: nextVisible,
    height: nextHeight,
    layoutMode,
    duration,
  });
}
